/**
 * The small masters the enquiry screens need before they can offer a choice: companies, customer
 * departments, delivery locations, and who is signed in.
 *
 * `useOwnCompanies` and `useDeliveryLocations` are read through `/api/resource` because neither has
 * a `quoflow.*` method — same residual-surface category as the master pickers in
 * `useEnquiryFromMail.ts`. `useCompanies` and `useCustomerDepartments` DO have one
 * (`lookups.get_companies`, `masters.get_customer_departments`) and use it. All of them are tiny
 * (7, 10, 27 rows live) and effectively static within a session, hence the long `staleTime`.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { callGet } from './bosun'
import { apiBase } from './apiBase'
import { proxyBearer } from './frappeSession'
import { isFrappeAuth } from './authProvider'
import { parseCurrentUser, type CurrentUser } from './currentUser'
import type { PickerOption } from './useEnquiryFromMail'
import { getResource } from './frappe'
import { str } from './mappers/coerce'
import { setCompanyCurrencies } from './baseCurrency'
import type { ExchangeRateRow } from '../exchangeRates'
import { trimmed, type WireList } from './wire'

// Re-exported so `CurrentUser` stays importable from the hook that produces it. Nothing outside
// this file imports the type today (verified 2026-08-08) — this is API stability, not a fix.
export type { CurrentUser }

/** Master data does not change while someone is reading a mail. */
const MASTER_STALE_MS = 30 * 60_000

/** Comfortably above the live counts; these are masters, not paginated lists. */
const MASTER_LIMIT = 200

function names(rows: Record<string, unknown>[]): string[] {
  return rows.map((row) => str(row.name)).filter((name) => name !== '')
}

/* ============ lookups.get_delivery_locations ============ */

/**
 * Which selling price list each delivery location implies.
 *
 * Live shape 2026-09-14: `{type: "delivery_location", data: [{name, location_name, price_list}]}`
 * — `BAHARIN → STANDARD SELLING PRICE`, `MALAYSIA → USD`, and so on. The enquiry has its own
 * `price_list` field; the header card writes this value into it when the location changes, so
 * the record says which list the quotation priced from rather than leaving it implied.
 *
 * A location with no price list is left OUT of the map, not mapped to `''`: the tag beside the
 * location should be absent, and the write should send `null`, when there is nothing to say.
 */
export function toDeliveryLocationPriceLists(wire: unknown): Map<string, string> {
  const out = new Map<string, string>()
  const rows = (wire as { data?: unknown } | null)?.data
  if (!Array.isArray(rows)) return out
  for (const row of rows as Record<string, unknown>[]) {
    const name = str(row.name)
    const priceList = str(row.price_list)
    if (name === '' || priceList === '') continue
    out.set(name, priceList)
  }
  return out
}

export function useDeliveryLocationPriceLists() {
  return useQuery({
    queryKey: ['masters', 'delivery-location-price-lists'] as const,
    queryFn: async () => toDeliveryLocationPriceLists(await callGet<unknown>('lookups', 'get_delivery_locations')),
    staleTime: MASTER_STALE_MS,
  })
}

/**
 * Every `Company` in this ERP. All of them are OUR OWN group (confirmed by the business
 * 2026-08-06), so any customer candidate matching one is the chandler, not a buyer — see
 * `lib/ownCompanies.ts` for why that guard exists and why it fails open when this list is empty.
 */
export function useOwnCompanies() {
  return useQuery({
    queryKey: ['masters', 'companies'] as const,
    queryFn: async () => names((await getResource<Record<string, unknown>>('Company', {
      fields: ['name'],
      limit: MASTER_LIMIT,
    })).data),
    staleTime: MASTER_STALE_MS,
  })
}

/**
 * The seven companies, with the currency each one keeps its books in.
 *
 * `lookups.get_companies` rather than `/api/resource/Company` (which `useOwnCompanies` above still
 * uses for its own different purpose): it is a first-class `quoflow.*` method, and it carries
 * `default_currency` — the thing that makes this list more than a picklist. Measured 2026-08-08 it
 * returns seven rows across FOUR currencies (AED, BHD, SAR, INR), which is precisely why labelling
 * every amount `AED` app-wide is wrong (see `baseCurrency.ts`).
 *
 * `name` IS the docname a `Link → Company` must be written with, punctuation and all
 * (`AQUA MARIS SHIPS SUPPLY -(S.P.S - L.L.C)`); never tidy it.
 */
export interface CompanyOption {
  name: string
  /** `''` when the company has none set — a real answer, not a missing field. */
  default_currency: string
  country: string
}

interface WireCompany {
  name?: string
  default_currency?: string
  country?: string
  abbr?: string
}

/** Pure, so the wire shape is pinned by a test rather than only through a mocked `fetch`. */
export function toCompanyOptions(rows: WireCompany[] | undefined): CompanyOption[] {
  return (rows ?? [])
    .map((row) => ({
      name: str(row.name),
      default_currency: str(row.default_currency),
      country: str(row.country),
    }))
    .filter((row) => row.name !== '')
}

export function useCompanies() {
  return useQuery({
    queryKey: ['masters', 'companies', 'lookups'] as const,
    queryFn: async (): Promise<CompanyOption[]> =>
      toCompanyOptions((await callGet<WireList<WireCompany>>('lookups', 'get_companies')).data),
    staleTime: MASTER_STALE_MS,
  })
}

/**
 * `useCompanies` in the shape `LinkCombo` wants.
 *
 * Filters CLIENT-side, unlike every picker in `useEnquiryFromMail.ts`: there are seven companies,
 * so one cached fetch beats a request per keystroke. The label carries the default currency,
 * because under multi-company that is the consequence of the choice — picking
 * `BEUREX TRADING COMPANY W.L.L` means the enquiry's money is BHD, and the docname alone does not
 * say so.
 */
export function toCompanyPickerOptions(companies: CompanyOption[], search: string): PickerOption[] {
  const term = search.trim().toLowerCase()
  return companies
    .filter((c) => term === '' || c.name.toLowerCase().includes(term))
    .map((c) => ({
      name: c.name,
      label: c.default_currency === '' ? c.name : `${c.name} — ${c.default_currency}`,
    }))
}

export function useCompanyPicker(search: string): { data?: PickerOption[]; isPending: boolean } {
  const { data, isPending } = useCompanies()
  const options = useMemo(() => (data === undefined ? undefined : toCompanyPickerOptions(data, search)), [data, search])
  return { data: options, isPending }
}

/** The `Delivery Location` picklist — mandatory on `create_enquiry`, and a Link, so only a value
 * from this list can be sent. */
export function useDeliveryLocations() {
  return useQuery({
    queryKey: ['masters', 'delivery-locations'] as const,
    queryFn: async () => names((await getResource<Record<string, unknown>>('Delivery Location', {
      fields: ['name'],
      limit: MASTER_LIMIT,
      orderBy: 'name asc',
    })).data),
    staleTime: MASTER_STALE_MS,
  })
}

/**
 * Who is signed in, and what Bosun role they hold.
 *
 * ---- Why this replaced a raw `frappe.auth.get_logged_user` call ----
 *
 * We added that call this morning to fix `User 'Akhil' not found` — the create-from-mail bar was
 * writing `useSession().user`, which is `ROLE_USERS[role]`, i.e. the literal sample names
 * `Priya`/`Akhil`/`Manoj` from `docs/DATA-MODEL.md`. It got us an email and nothing else.
 *
 * `auth.get_current_user` (Complete API v2, 2026-08-06) gives the email AND the **Bosun role**,
 * already mapped from the Frappe roles server-side — which is ask `A5`, the thing `SessionProvider`
 * has been guessing locally since the migration. Verified live:
 *
 *     {email: "rfq@gmail.com", full_name: "rfq", enabled: 1,
 *      role: "purchase_manager", frappe_roles: ["Purchase Manager", …],
 *      sales_person: null, language: "en"}
 *
 * `role` comes back as one of our own three values, so nothing here has to map anything.
 *
 * A failure resolves to `null` rather than a guess: a caller must then omit the field instead of
 * sending a name the server will reject, which is exactly the bug this line of work started on.
 */

/**
 * `GET /api/whoami` — the production proxy's own route (proxy design §4.4), tried FIRST.
 *
 * Deployed, `auth.get_current_user` cannot answer this question: every request reaches Frappe under
 * the shared service token, so it would report that token's user (`rfq@gmail.com`) for everybody —
 * and `SessionProvider`, the Users-screen gate, `scopeForRole` and every `canDecide` check would
 * then be reasoning about the wrong person. `server/index.ts` verifies the Clerk JWT and answers
 * with the caller's real Frappe identity, in the SAME shape, so `parseCurrentUser` is unchanged.
 *
 * `null` means "no proxy here" — in dev there is none, and the caller falls back to the Frappe
 * method below. Anything other than a clean 404 is left to the fallback too: a 401 from the proxy
 * is a real answer ("you are not signed in"), but retrying it against Frappe costs one request and
 * fails honestly rather than stranding the app with no identity at all.
 */
async function whoami(): Promise<CurrentUser | null> {
  // `/api/whoami` is the Waves proxy's own route. With Clerk off the app is served BY the Frappe
  // site, where that path is not ours and answers a 404 page — one wasted request per session,
  // and a confusing one in the network log. `auth.get_current_user` is the source there.
  if (isFrappeAuth()) return null
  try {
    const res = await fetch(apiBase() + '/api/whoami', {
      credentials: 'include',
      headers: await proxyAuthHeaders(),
    })
    if (!res.ok) return null
    const body = (await res.json()) as { message?: unknown } | null
    return parseCurrentUser(body?.message)
  } catch {
    return null
  }
}

/** `/api/whoami` is not a `quoflow.*` method, so it bypasses `bosun.ts` — which means it is the
 * one call site that has to attach the bearer itself (proxy design §4.4). */
async function proxyAuthHeaders(): Promise<Record<string, string>> {
  const bearer = await proxyBearer()
  return bearer ? { Authorization: `Bearer ${bearer}` } : {}
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ['current-user'] as const,
    queryFn: async (): Promise<CurrentUser | null> =>
      (await whoami()) ?? parseCurrentUser(await callGet<unknown>('auth', 'get_current_user')),
    // Cannot change without a reload.
    staleTime: Infinity,
    retry: false,
  })
}

/**
 * `Customer Department` for one enquiry type — the list a purchaser may choose from.
 *
 * Uses the `quoflow.*` method rather than the doctype, because the FILTERING is the point:
 * measured 2026-08-08, `?enquiry_type=Provision` returns Food / Grocery / Sea Foods, while
 * `Technical` returns a different and much longer list (BATTERIES, Bridge, CABIN…). Omitting the
 * parameter returns everything, which is not what any caller here wants.
 *
 * `enquiry_type` of `null` disables the query rather than fetching the unfiltered list — an
 * enquiry whose type has not loaded yet must not briefly offer departments from the wrong one.
 */
export function useCustomerDepartments(enquiryType: string | null | undefined) {
  return useQuery({
    queryKey: ['masters', 'customer-departments', enquiryType ?? ''] as const,
    queryFn: async () => {
      const res = await callGet<WireList<{ name?: string; department?: string }>>(
        'masters',
        'get_customer_departments',
        { enquiry_type: enquiryType },
      )
      // `name` IS the docname and the value a Link field must be written with; `department` is the
      // label and is identical on every row measured. Preferring `name` keeps identity and display
      // from drifting the day they stop matching.
      return (res.data ?? []).map((row) => str(row.name) || str(row.department)).filter((v) => v !== '')
    },
    enabled: Boolean(enquiryType),
    staleTime: MASTER_STALE_MS,
  })
}

/* ============ sales_person.get_salesperson ============ */

/**
 * The sales people who may be put on THIS enquiry — filtered by its customer and company.
 *
 * The picker used to read the whole `Sales Person` master unfiltered, which offered everyone in
 * the group regardless of which legal entity the enquiry belongs to or who actually handles that
 * customer. `Sales Person` carries a `company` Link (schema, `docs/frappe-schema/`), and the
 * Frappe team supplied a method that resolves both sides at once (fix list point 4):
 *
 *     GET sales_person.get_salesperson?customer=TEAM SHIPPING CO (LLC)&company=AQUA MARIS …
 *
 * ---- The shape, MEASURED live 2026-08-28 against rfqdev.etoserp.com ----
 *
 *     ?customer=TEAM SHIPPING CO (LLC)&company=AQUA MARIS SHIPS SUPPLY -(S.P.S - L.L.C)
 *       → {"message": {"sales_person": "KARTHIKA"}}
 *     ?customer=&company=…  /  no params  /  unknown company
 *       → {"message": {"sales_person": null}}
 *
 * `bosun.ts` strips the `message` envelope, so what arrives here is `{"sales_person": "KARTHIKA"}`.
 * ONE person, not a list — the method answers "who handles this customer at this company", and
 * there is a single answer. `null` is a real answer meaning nobody is mapped, and is mapped to NO
 * options rather than to a blank one.
 *
 * ⚠️ This method answers **without authentication** (`allow_guest`), measured the same day: an
 * unauthenticated GET returns the salesperson's name, while every sibling method on the namespace
 * returns `PermissionError` to a guest. Filed as `D16` in `docs/bosun-frappe-asks.md`. Nothing here depends
 * on that — the app is authenticated regardless — but do not let it become a reason to skip auth.
 *
 * The array shapes below are tolerance, not speculation about the primary case: this endpoint is
 * new, its siblings on the namespace return a `WireList` envelope (`masters.get_suppliers`) or a
 * bare array (`items.get_purchase_history`), and accepting all three costs nothing while a
 * wrong guess would render an empty picker.
 *
 * The DOCNAME is what a Link field writes, so a row with no resolvable docname is dropped — a
 * label-only option would fail on save with a LinkValidationError rather than simply not match.
 */
export function toSalesPersonOptions(payload: unknown): PickerOption[] {
  // The single-value shape the method actually returns, before any list handling.
  if (payload !== null && typeof payload === 'object' && !Array.isArray(payload)) {
    const single = payload as Record<string, unknown>
    if ('sales_person' in single && !Array.isArray(single.sales_person)) {
      const name = trimmed(single.sales_person)
      // `''`/null is a real answer — this customer has nobody assigned — not a blank option.
      return name === '' ? [] : [{ name, label: name }]
    }
  }

  const rows: unknown[] = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { data?: unknown })?.data)
      ? ((payload as { data: unknown[] }).data)
      : []

  const out: PickerOption[] = []
  for (const row of rows) {
    if (typeof row === 'string') {
      // `trimmed`, not `str`: this namespace sends a leading space on name-ish fields (the
      // defect `wire.ts`'s `trimmed` exists for), and a Link value carrying one does not match
      // its docname on save.
      const name = trimmed(row)
      if (name !== '') out.push({ name, label: name })
      continue
    }
    if (typeof row !== 'object' || row === null) continue
    const r = row as Record<string, unknown>
    const name = trimmed(r.name) || trimmed(r.sales_person)
    if (name === '') continue
    const label = trimmed(r.sales_person_name)
    out.push({ name, label: label !== '' && label !== name ? `${name} — ${label}` : name })
  }
  return out
}

/**
 * The scoped answer FIRST, then the rest of the master — never the scoped answer alone.
 *
 * `get_salesperson` resolves a single person, so offering only that would turn a picker into a
 * locked field: an enquiry that needs somebody else (cover, a correction, a customer whose
 * mapping is stale) would have no way to say so. The resolved person is marked "handles this
 * customer" and sorted to the top, which is the whole benefit of the lookup, while every other
 * sales person stays one scroll away.
 *
 * With no customer/company to scope by, the master is simply offered unmarked — an enquiry whose
 * header is half-filled still needs a usable picker, and an empty list would read as "this
 * customer has nobody", a claim neither half of a missing filter can support.
 */
/**
 * The ONE sales person `get_salesperson` resolves for this customer + company, or `null`.
 *
 * Separate from `useSalesPeople` below, which folds that answer into a full picker list. This is
 * the answer on its own, so a caller can APPLY it rather than merely offer it — the recommendation
 * was being shown at the top of a dropdown and then left for the user to select by hand, which is
 * a click to agree with something the server already decided.
 *
 * `null` unless the lookup resolved EXACTLY ONE person. Two or more is a genuine choice and must
 * stay a choice; zero is nothing to apply. Shares `useSalesPeople`'s query key, so this costs no
 * extra request wherever both are used.
 */
export function useRecommendedSalesPerson(customer: string, company: string) {
  const filtered = customer.trim() !== '' && company.trim() !== ''
  const scoped = useQuery({
    queryKey: ['masters', 'sales-people', customer, company] as const,
    queryFn: async () =>
      toSalesPersonOptions(await callGet<unknown>('sales_person', 'get_salesperson', { customer, company })),
    enabled: filtered,
    staleTime: MASTER_STALE_MS,
  })
  const options = scoped.data ?? []
  return {
    recommended: options.length === 1 ? options[0].name : null,
    // `isLoading`, never `isPending` — a DISABLED query sits at `pending` forever in React Query
    // v5, so a caller gating on `isPending` would wait for a lookup that never runs.
    isLoading: scoped.isLoading,
    /**
     * The lookup RAN and answered with nobody — distinct from "not answered yet" and from
     * "failed", both of which must leave the field alone. This is what lets a customer change
     * clear a sales person who handled the previous customer (`lib/salesPersonDefault.ts`).
     */
    resolvedNobody: filtered && scoped.isSuccess && options.length === 0,
  }
}

export function useSalesPeople(customer: string, company: string, search: string) {
  const filtered = customer.trim() !== '' && company.trim() !== ''

  const scoped = useQuery({
    queryKey: ['masters', 'sales-people', customer, company] as const,
    queryFn: async () =>
      toSalesPersonOptions(await callGet<unknown>('sales_person', 'get_salesperson', { customer, company })),
    enabled: filtered,
    staleTime: MASTER_STALE_MS,
  })

  /*
    The whole `Sales Person` master, always — it is one small request per session (30-minute
    `staleTime`, ~200 rows) and it is what keeps the field editable when the scoped answer is
    wrong or missing.
  */
  const all = useQuery({
    queryKey: ['picker', 'Sales Person', ''] as const,
    queryFn: async () => {
      const page = await getResource<Record<string, unknown>>('Sales Person', {
        fields: ['name'],
        limit: MASTER_LIMIT,
        orderBy: 'name asc',
      })
      return toSalesPersonOptions(page.data)
    },
    staleTime: MASTER_STALE_MS,
  })

  // Narrowed in the browser, not by a second request: the master is small enough to hold whole,
  // and `get_salesperson` takes no search term.
  const term = search.trim().toLowerCase()

  const data = useMemo(() => {
    const resolved = (scoped.data ?? []).map((o) => ({ ...o, note: 'handles this customer' }))
    const resolvedNames = new Set(resolved.map((o) => o.name))
    // De-duplicated by DOCNAME: the resolved person is in the master too, and listing them twice
    // would offer the same Link value under two rows.
    const merged = [...resolved, ...(all.data ?? []).filter((o) => !resolvedNames.has(o.name))]
    return term === '' ? merged : merged.filter((o) => o.label.toLowerCase().includes(term))
  }, [scoped.data, all.data, term])

  // Pending only while nothing is showable yet. The master alone is a usable list, so a slow
  // scoped lookup must not hold the picker in a spinner.
  return { data, isPending: all.isPending && (!filtered || scoped.isPending) }
}

/**
 * Every company's base currency, installed into `baseCurrency.ts` for the mappers to read.
 *
 * Read from the `Company` doctype rather than `data_ops.get_company_default_currency`, which
 * returns a SUBSET — measured 2026-08-08, the method listed five companies while the doctype has
 * seven, omitting AQUA MARIS (our own fallback company) and BEUREX. A currency map missing the
 * company an enquiry actually belongs to would silently fall back to the app-wide value, which is
 * the bug this exists to fix.
 */
export function useCompanyCurrencies() {
  return useQuery({
    queryKey: ['masters', 'company-currencies'] as const,
    queryFn: async () => {
      const page = await getResource<{ name?: string; default_currency?: string }>('Company', {
        fields: ['name', 'default_currency'],
        limit: MASTER_LIMIT,
      })
      const entries = page.data
        .map((row) => [str(row.name), str(row.default_currency)] as const)
        .filter(([name, currency]) => name !== '' && currency !== '')
      setCompanyCurrencies(entries)
      return new Map(entries)
    },
    staleTime: MASTER_STALE_MS,
  })
}

/**
 * Every `Currency Exchange` row. `pickRate` (`lib/exchangeRates.ts`) chooses among them — this hook
 * only fetches, because the choice is a commercial one worth keeping visible and testable.
 */
export function useExchangeRates() {
  return useQuery({
    queryKey: ['masters', 'exchange-rates'] as const,
    queryFn: async () => {
      const res = await callGet<{ data?: ExchangeRateRow[] }>('data_ops', 'get_exchange_rates', {})
      return res.data ?? []
    },
    staleTime: MASTER_STALE_MS,
  })
}

/**
 * One customer's own currency, or `''` when they have none on file.
 *
 * `''` is a real answer — 1371 of 1561 customers have no `default_currency` — and it means "quote
 * in the company's currency", not "we failed to look". Never defaulted to the company's currency
 * here, so the caller can tell the two apart.
 */
export function useCustomerCurrency(customer: string | null | undefined) {
  return useQuery({
    queryKey: ['masters', 'customer-currency', customer ?? ''] as const,
    queryFn: async () => {
      const page = await getResource<{ default_currency?: string }>('Customer', {
        filters: [['name', '=', customer ?? '']],
        fields: ['default_currency'],
        limit: 1,
      })
      return str(page.data[0]?.default_currency)
    },
    enabled: !!customer,
    staleTime: MASTER_STALE_MS,
  })
}
